/* eslint-disable react-hooks/exhaustive-deps */

import React, { useEffect, useState } from 'react'
import { FormikProvider, useFormik } from 'formik'
import { useRouter } from 'next/router'
import Button from '@material-ui/core/Button'
import { useMediaQuery, useTheme } from '@material-ui/core'
import {
  TranslationDictionary,
  pushDangerNotification,
  useQueryUser,
  useTranslation
} from '@plandi/common'

import {
  URLS,
  useQueryMyProfileCompany,
  useQueryMyProfileFreelancer
} from 'src/common'
import { useThunkDispatch } from 'src/store'
import {
  FormValuesProjectRequest,
  getSaveProjectValidationSchema,
  ProjectForm,
  useMutationSaveDraftProject
} from '../common'
import { LoadinAction } from 'src/reducers/actions/LoadingActions'
import styles from './style.module.scss'
import { CreateSelectModal } from 'src/common/create-select-modal'

type ProjectCreateProps = {
  translate?: TranslationDictionary['data']
  locale: string
  onChangeShowBreadCrumbs: (value: boolean) => void
}

export const ProjectCreate: React.FC<ProjectCreateProps> = ({
  onChangeShowBreadCrumbs,
  locale
}) => {
  const [isPreviewMode, setIsPreviewMode] = useState(false)
  const [selectModalVisible, setSelectModalVisible] = useState(true)
  const { data: translate } = useTranslation()
  const { data: user } = useQueryUser(locale)

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'))
  const router = useRouter()
  const dispatch = useThunkDispatch()

  const { data: profileCompany, isLoading: isLoadingCompany } =
    useQueryMyProfileCompany()

  const { data: profileFreelancer, isLoading: isLoadingFreelancer } =
    useQueryMyProfileFreelancer()

  const formik = useFormik<FormValuesProjectRequest>({
    initialValues: {
      name: translate?.shop['unnamed-project'] ?? '',
      cost: null,
      negotiated: false,
      designYear: null,
      categoryId: null,
      subCategoryId: null,
      projectStaging: undefined,
      expertise: false,
      bimModel: false,
      fileFormats: [],
      landArea: null,
      builtUpArea: null,
      buildingArea: null,
      buildingHeight: null,
      numberOfFloors: null,
      appointment: '',
      projectComposition: '',
      notes: '',
      parts: [],
      status: 'draft',
      cover: undefined,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      images: undefined,
      userId: 0,
      profileId: 0
    },
    validationSchema: getSaveProjectValidationSchema(translate),
    onSubmit: (values) => {
      dispatch(LoadinAction(true))
      mutationSaveDraftProject.mutate({
        ...values,
        cost: values.cost ? Number(values.cost) : null,
        designYear: values.designYear ? Number(values.designYear) : null,
        landArea: values.landArea ? Number(values.landArea) : null,
        builtUpArea: values.builtUpArea ? Number(values.builtUpArea) : null,
        buildingArea: values.buildingArea ? Number(values.buildingArea) : null,
        buildingHeight: values.buildingHeight
          ? Number(values.buildingHeight)
          : null,
        numberOfFloors: values.numberOfFloors
          ? Number(values.numberOfFloors)
          : null
      })
    }
  })

  useEffect(() => {
    if (user) formik.setFieldValue('userId', user.id)
  }, [user])

  useEffect(() => {
    setSelectModalVisible(router.query.type ? false : true)
    onChangeShowBreadCrumbs(router.query.type ? true : false)
  }, [router.query])

  const mutationSaveDraftProject = useMutationSaveDraftProject(
    translate as NonNullable<typeof translate>,
    {
      onSuccess: () => {
        dispatch(LoadinAction(false))
        formik.resetForm()
        router.push(URLS.projects.me)
      },
      onError: (error) => {
        dispatch(
          pushDangerNotification(error.response?.data?.message || error.message)
        )
        dispatch(LoadinAction(false))

        formik.setFieldValue('status', 'draft')
      },
      onSettled: () => {
        dispatch(LoadinAction(false))
        formik.setSubmitting(false)
      }
    }
  )

  const handlePublishProject = async () => {
    formik.setFieldValue('status', 'published')

    const values = {
      ...formik.values,
      status: 'published' as const
    }

    const errors = await formik.validateForm(values)

    const isValid = Object.keys(errors).length === 0

    if (!isValid) {
      dispatch(
        pushDangerNotification(translate?.shop['required-not-filled'] as string)
      )

      return
    }

    formik.handleSubmit()
  }

  const handleDraftProject = async () => {
    const values = {
      ...formik.values,
      status: 'draft' as const
    }

    const errors = await formik.validateForm(values)

    const isValid = Object.keys(errors).length === 0

    if (isValid) {
      formik.setFieldValue('status', 'draft')

      formik.handleSubmit()
    }
  }

  if (selectModalVisible)
    return (
      <CreateSelectModal
        type="project"
        isCompanyLoading={isLoadingCompany}
        isFreelancerLoading={isLoadingFreelancer}
        profileCompany={profileCompany}
        profileFreelancer={profileFreelancer}
        closeModal={() => setSelectModalVisible(false)}
      />
    )

  return (
    <FormikProvider value={formik}>
      <ProjectForm
        isPreviewMode={isPreviewMode}
        setIsPreviewMode={setIsPreviewMode}
        buttons={
          <>
            {isMobile ? (
              <div className={styles.btnGroup}>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={formik.isSubmitting}
                  onClick={handleDraftProject}
                  className={styles.saveDraftBtn}
                >
                  {translate?.['save-to-draft']}
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handlePublishProject}
                  className={styles.savePublishBtn}
                  disabled={formik.isSubmitting || !formik.dirty}
                >
                  {translate?.shop['save-and-publish']}
                </Button>
              </div>
            ) : (
              <>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={formik.isSubmitting}
                  onClick={handleDraftProject}
                >
                  {translate?.shop['add-model-page']['save-to-draft']}
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handlePublishProject}
                  disabled={formik.isSubmitting || !formik.dirty}
                >
                  {translate?.shop['save-and-publish']}
                </Button>
              </>
            )}
          </>
        }
      />
    </FormikProvider>
  )
}
